import React, { useState, useEffect, useRef } from 'react';
import { Book, ChevronDown, Search } from 'lucide-react';
import { bibleBooks } from '../lib/bibleBooks';
import { useStore } from '../store';

interface BibleNavigationProps {
  selectedBook?: string;
  selectedChapter?: number;
  onSelectBook: (book: string) => void;
  onSelectChapter: (chapter: number) => void;
}

export function BibleNavigation({
  selectedBook: propSelectedBook,
  selectedChapter: propSelectedChapter,
  onSelectBook,
  onSelectChapter
}: BibleNavigationProps) {
  const [isBookDropdownOpen, setIsBookDropdownOpen] = useState(false);
  const [isChapterDropdownOpen, setIsChapterDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const bookDropdownRef = useRef<HTMLDivElement>(null);
  const chapterDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const storedBook = useStore(state => state.selectedBook);
  const storedChapter = useStore(state => state.selectedChapter);

  // Use stored values as fallbacks
  const selectedBook = propSelectedBook || storedBook;
  const selectedChapter = propSelectedChapter || storedChapter;

  // Helper function to normalize book names for comparison
  const normalizeBookName = (name: string | undefined) => {
    if (!name) return '';
    // Remove extra spaces, convert to lowercase
    return name.trim().toLowerCase()
      // Normalize numbered books (e.g., "1 john" and "1john" should match)
      .replace(/^(\d+)\s*/, '$1');
  };

  const toggleBookDropdown = () => {
    setIsBookDropdownOpen(!isBookDropdownOpen);
    // Close chapter dropdown if it's open
    if (isChapterDropdownOpen) {
      setIsChapterDropdownOpen(false);
    }
    // Reset search and highlight when opening
    if (!isBookDropdownOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  const toggleChapterDropdown = () => {
    setIsChapterDropdownOpen(!isChapterDropdownOpen);
    // Close book dropdown if it's open
    if (isBookDropdownOpen) {
      setIsBookDropdownOpen(false);
    }
  };

  const oldTestamentBooks = bibleBooks.filter(book => book.testament === 'old');
  const newTestamentBooks = bibleBooks.filter(book => book.testament === 'new');
  
  // Filter books based on search query
  const filteredOldTestament = oldTestamentBooks.filter(book => 
    normalizeBookName(book.name).includes(normalizeBookName(searchQuery))
  );
  const filteredNewTestament = newTestamentBooks.filter(book => 
    normalizeBookName(book.name).includes(normalizeBookName(searchQuery))
  );
  
  const currentBook = bibleBooks.find(b => normalizeBookName(b.name) === normalizeBookName(selectedBook));
  const chapters = currentBook ? Array.from({ length: currentBook.chapters }, (_, i) => i + 1) : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bible-navigation')) {
        setIsBookDropdownOpen(false);
        setIsChapterDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    // Focus search input when book dropdown opens
    if (isBookDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isBookDropdownOpen]);

  const handleBookSelect = (bookName: string) => {
    onSelectBook(bookName);
    setIsBookDropdownOpen(false);
    setSearchQuery('');
  };

  const handleChapterSelect = (chapter: number) => {
    onSelectChapter(chapter);
    setIsChapterDropdownOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isBookDropdownOpen) {
      const totalBooks = filteredOldTestament.length + filteredNewTestament.length;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => (prev + 1) % totalBooks);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => (prev - 1 + totalBooks) % totalBooks);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            const allBooks = [...filteredOldTestament, ...filteredNewTestament];
            if (allBooks[highlightedIndex]) {
              handleBookSelect(allBooks[highlightedIndex].name);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsBookDropdownOpen(false);
          setSearchQuery('');
          break;
      }
    } else if (isChapterDropdownOpen) {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          handleChapterSelect(Math.min(selectedChapter + 1, chapters.length));
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handleChapterSelect(Math.max(selectedChapter - 1, 1));
          break;
        case 'Escape':
          e.preventDefault();
          setIsChapterDropdownOpen(false);
          break;
      }
    }
  };

  return (
    <div className="flex flex-wrap gap-2 sm:gap-4 bible-navigation" onKeyDown={handleKeyDown}>
      {/* Book Selection */}
      <div className="relative flex-1" ref={bookDropdownRef}>
        <button
          onClick={toggleBookDropdown}
          className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Select Bible Book"
          aria-expanded={isBookDropdownOpen}
          aria-controls="book-dropdown"
        >
          <div className="flex items-center gap-2">
            <Book className="text-gold-500" size={20} />
            <span className="text-white">{selectedBook || 'Select Book'}</span>
          </div>
          <ChevronDown className={`text-white/60 transition-transform ${isBookDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isBookDropdownOpen && (
          <div 
            id="book-dropdown"
            className="absolute z-50 w-full mt-2 py-2 bg-obsidian-800 rounded-lg shadow-lg border border-white/10 max-h-[calc(100vh-200px)] overflow-y-auto"
            role="listbox"
          >
            {/* Search Input */}
            <div className="sticky top-0 px-4 py-2 bg-obsidian-800 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search books..."
                  className="w-full pl-9 pr-4 py-1.5 bg-white/5 text-white placeholder:text-white/40 rounded-md focus:outline-none focus:ring-1 focus:ring-gold-500/50"
                  aria-label="Search Bible books"
                />
              </div>
            </div>

            {filteredOldTestament.length > 0 && (
              <div className="px-4 py-2">
                <h3 className="text-sm font-medium text-white/60">Old Testament</h3>
                <div className="mt-2 space-y-1">
                  {filteredOldTestament.map((book, index) => (
                    <button
                      key={book.name}
                      onClick={() => handleBookSelect(book.name)}
                      className={`w-full text-left px-3 py-1.5 rounded transition-colors ${
                        normalizeBookName(book.name) === normalizeBookName(selectedBook)
                          ? 'bg-gold-500 text-white'
                          : index === highlightedIndex
                          ? 'bg-white/10 text-white'
                          : 'text-white hover:bg-white/5'
                      }`}
                      role="option"
                      aria-selected={normalizeBookName(book.name) === normalizeBookName(selectedBook)}
                    >
                      {book.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {filteredNewTestament.length > 0 && (
              <div className="mt-2 px-4 py-2">
                <h3 className="text-sm font-medium text-white/60">New Testament</h3>
                <div className="mt-2 space-y-1">
                  {filteredNewTestament.map((book, index) => (
                    <button
                      key={book.name}
                      onClick={() => handleBookSelect(book.name)}
                      className={`w-full text-left px-3 py-1.5 rounded transition-colors ${
                        normalizeBookName(book.name) === normalizeBookName(selectedBook)
                          ? 'bg-gold-500 text-white'
                          : index === (highlightedIndex - filteredOldTestament.length)
                          ? 'bg-white/10 text-white'
                          : 'text-white hover:bg-white/5'
                      }`}
                      role="option"
                      aria-selected={normalizeBookName(book.name) === normalizeBookName(selectedBook)}
                    >
                      {book.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {filteredOldTestament.length === 0 && filteredNewTestament.length === 0 && (
              <div className="px-4 py-2 text-white/60 text-center">
                No books found matching "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chapter Selection */}
      <div className="relative" ref={chapterDropdownRef}>
        <button
          onClick={toggleChapterDropdown}
          disabled={!selectedBook}
          className={`w-full min-w-[100px] flex items-center justify-between gap-2 px-4 py-2 rounded-lg transition-colors ${
            selectedBook
              ? 'bg-white/5 hover:bg-white/10 text-white'
              : 'bg-white/5 text-white/40 cursor-not-allowed'
          }`}
          aria-label="Select Bible Chapter"
          aria-expanded={isChapterDropdownOpen}
          aria-controls="chapter-dropdown"
        >
          <span>{selectedChapter ? `Chapter ${selectedChapter}` : 'Chapter'}</span>
          <ChevronDown className={`text-white/60 transition-transform ${isChapterDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isChapterDropdownOpen && (
          <div
            id="chapter-dropdown"
            className="absolute z-50 right-0 w-32 mt-2 py-2 bg-obsidian-800 rounded-lg shadow-lg border border-white/10 max-h-[calc(100vh-200px)] overflow-y-auto"
            role="listbox"
          >
            <div className="px-4 py-2">
              <div className="space-y-1">
                {chapters.map((chapter) => (
                  <button
                    key={chapter}
                    onClick={() => handleChapterSelect(chapter)}
                    className={`w-full text-left px-3 py-1.5 rounded transition-colors ${
                      chapter === selectedChapter
                        ? 'bg-gold-500 text-white'
                        : 'text-white hover:bg-white/5'
                    }`}
                    role="option"
                    aria-selected={chapter === selectedChapter}
                  >
                    {chapter}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}